import { useEffect, useState } from 'react';
import Icon from './Icon';
import { QUOTES } from '../data';
import { IMG, u } from '../images';

// Portraits matched to QUOTES by index (Elena, Marcus, Priya).
const FACES = [IMG.team[1], IMG.team[2], IMG.team[3]];

export default function Testimonials() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % QUOTES.length), 7000);
    return () => clearInterval(t);
  }, [paused]);

  const step = (d) => setI((n) => (n + d + QUOTES.length) % QUOTES.length);

  return (
    <section className="section bg-ink text-white">
      <div className="grid-bg pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-brand/40 blur-[130px]" data-parallax="0.3" />

      <div className="container-x relative grid items-center gap-14 lg:grid-cols-[.85fr_1.15fr]">
        {/* intro + proof */}
        <div data-anim="left">
          <span className="eyebrow eyebrow-dark">Testimonials</span>
          <h2 className="h2 mt-5" data-split>Trusted by <span className="text-accent-light">teams</span> who <span className="text-outline-light">ship</span></h2>
          <p className="mt-5 max-w-md text-lg leading-relaxed text-white/65">Don't take our word for it. Here is what the people we build with say about working with Origo.</p>

          <div className="mt-8 flex flex-wrap items-center gap-6">
            <div className="flex -space-x-3">
              {FACES.map((f) => (
                <img key={f} src={u(f, 120)} alt="" loading="lazy" className="h-12 w-12 rounded-full border-2 border-ink object-cover object-[center_8%]" />
              ))}
              <span className="grid h-12 w-12 place-items-center rounded-full border-2 border-ink bg-brand-lime text-xs font-bold text-ink">60+</span>
            </div>
            <div>
              <p className="flex items-center gap-1 text-brand-lime">
                {[0, 1, 2, 3, 4].map((s) => <Icon key={s} name="star" className="h-4 w-4 fill-current" />)}
                <span className="ml-2 font-display text-lg font-bold text-white">4.9 / 5</span>
              </p>
              <p className="text-sm text-white/60">from 60+ happy clients</p>
            </div>
          </div>

          <div className="mt-10 flex gap-3">
            <button onClick={() => step(-1)} aria-label="Previous testimonial" className="grid h-12 w-12 rotate-180 place-items-center rounded-full border border-white/20 transition-colors hover:border-brand-lime hover:text-brand-lime"><Icon name="arrow" className="h-5 w-5" /></button>
            <button onClick={() => step(1)} aria-label="Next testimonial" className="grid h-12 w-12 place-items-center rounded-full bg-brand-lime text-ink transition-transform hover:-translate-y-0.5"><Icon name="arrow" className="h-5 w-5" /></button>
          </div>
        </div>

        {/* featured quote + selector */}
        <div data-anim="right" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <div className="relative rounded-[2rem] rounded-tr-[5rem] bg-brand-soft p-8 text-ink shadow-2xl sm:p-12">
            <span className="absolute -top-6 left-8 grid h-14 w-14 place-items-center rounded-2xl bg-brand-lime text-ink shadow-xl sm:left-12"><Icon name="quote" className="h-7 w-7" /></span>
            <div className="relative mt-4 min-h-[300px] sm:min-h-[250px]" aria-live="polite">
              {QUOTES.map((q, idx) => (
                <figure
                  key={q.name}
                  className={`absolute inset-0 flex flex-col justify-between transition-all ${idx === i ? 'translate-y-0 opacity-100 delay-300 duration-500' : 'pointer-events-none translate-y-4 opacity-0 duration-300'}`}
                  aria-hidden={idx !== i}
                >
                  <blockquote className="font-display text-xl font-medium leading-relaxed sm:text-2xl">“{q.quote}”</blockquote>
                  <figcaption className="mt-6 flex items-center gap-4 border-t border-brand/15 pt-6">
                    <img src={u(FACES[idx], 160)} alt={q.name} loading="lazy" className="h-14 w-14 rounded-full object-cover object-[center_8%] ring-2 ring-brand" />
                    <span>
                      <span className="block font-display text-lg font-bold">{q.name}</span>
                      <span className="text-sm text-brand">{q.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            {QUOTES.map((q, idx) => (
              <button
                key={q.name}
                onClick={() => setI(idx)}
                aria-label={`Show testimonial from ${q.name}`}
                aria-pressed={idx === i}
                className={`flex items-center gap-3 rounded-2xl border p-3 text-left transition-all ${idx === i ? 'border-brand-lime bg-brand-lime/10' : 'border-white/10 bg-white/[0.04] hover:border-white/30'}`}
              >
                <img src={u(FACES[idx], 100)} alt="" loading="lazy" className="h-10 w-10 shrink-0 rounded-full object-cover object-[center_8%]" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold">{q.name}</span>
                  <span className="block truncate text-xs text-white/55">{q.role.split(', ')[1] || q.role}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
